/* global baseurl, token */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Lightweight adapter: use fetch to call legacy `baseurl` endpoints
async function apiGet(path) {
    const res = await fetch(`${baseurl}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Request failed');
    return json;
}

// Fetch all courses
export const fetchCourses = createAsyncThunk('courses/fetchCourses', async (_, { rejectWithValue }) => {
    try {
        const response = await apiGet(`/items/courses`);

        let courses = response.data.data;

        // Fetch topics details for each course
        const coursesWithTopics = await Promise.all(
            courses.map(async course => {
                if (!course.topics || course.topics.length === 0) {
                    return { ...course, topics: [] }; // Ensure topics is an empty array instead of undefined
                }

                const topicDetails = await Promise.all(course.topics.map(topicId => apiGet(`/items/topics/${topicId}`)));

                return { ...course, topics: topicDetails.map(res => res.data.data) };
            })
        );

        return coursesWithTopics;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch courses');
    }
});

// Fetch a single course with full details (topics & instructor)
export const fetchCourseDetails = createAsyncThunk('courses/fetchCourseDetails', async (courseId, { rejectWithValue }) => {
    try {
        const response = await apiGet(`/items/courses/${courseId}`);

        const course = response.data.data;
        console.log(response);

        // Fetch all topic details
        const topicDetails = await Promise.all((course.topics || []).map(topicId => apiGet(`/items/topics/${topicId}`)));

        const fullTopics = topicDetails.map(res => res.data.data);

        // Fetch instructor details if available
        let instructorDetails = null;
        if (course.instructor) {
            const instructorResponse = await apiGet(`/items/instructors/${course.instructor}`);
            instructorDetails = instructorResponse.data.data;
        }

        return { ...course, topics: fullTopics, instructor: instructorDetails };
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch course details');
    }
});

const coursesSlice = createSlice({
    name: 'courses',
    initialState: {
        items: [],
        status: 'idle',
        error: null,
        selectedCourse: null,
        courseDetails: null,
        courseStatus: 'idle',
        courseError: null,
    },
    reducers: {
        selectCourse: (state, action) => {
            state.selectedCourse = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            // Fetch all courses
            .addCase(fetchCourses.pending, state => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchCourses.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchCourses.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Fetch full course details
            .addCase(fetchCourseDetails.pending, state => {
                state.courseStatus = 'loading';
                state.courseError = null;
            })
            .addCase(fetchCourseDetails.fulfilled, (state, action) => {
                state.courseStatus = 'succeeded';
                state.courseDetails = action.payload;
            })
            .addCase(fetchCourseDetails.rejected, (state, action) => {
                state.courseStatus = 'failed';
                state.courseError = action.payload;
            });
    },
});

export const { selectCourse } = coursesSlice.actions;
export default coursesSlice.reducer;
